import MoveIcon from "./MoveIcon";
import Logo from "./Logo";
import { OutcomeDonut, MiniStat, AgreementBar, Outcome2x2Matrix } from "./OutcomeCharts";
import { TRUST_COLOR, BETRAY_COLOR, MIXED_COLOR, TEXT_MUTED, BORDER, GRADIENT_BG } from "../theme";

// The full "today's outcomes" shareable visual — donut + trust/mixed/betray
// breakdown, plus (if that day had a question) a second card with the
// agree-vs-disagree comparison and every answer-pair permutation. Shared by
// the admin's stats tab and the player-facing daily stats screen so both
// show exactly the same thing.
function DailyOutcomesCard({ stats, loading, dateLabel, emptyLabel = "no games played this day", topRounded = true }) {
  const outcomes = stats ? [
    { label: "both trust", count: stats.cc, color: TRUST_COLOR, a: "cooperate", b: "cooperate" },
    { label: "one betrayed", count: stats.cd, color: MIXED_COLOR, a: "cooperate", b: "defect" },
    { label: "both betrayed", count: stats.dd, color: BETRAY_COLOR, a: "defect", b: "defect" },
  ] : [];

  const breakdown = stats?.answerBreakdown;
  const agreedRate = breakdown && breakdown.agreedTotal > 0 ? Math.round((breakdown.agreedTrust / breakdown.agreedTotal) * 100) : null;
  const disagreedRate = breakdown && breakdown.disagreedTotal > 0 ? Math.round((breakdown.disagreedTrust / breakdown.disagreedTotal) * 100) : null;

  return (
    <>
      <div style={{ background: GRADIENT_BG, borderRadius: topRounded ? "16px" : "0 0 16px 16px", padding: "28px 20px", textAlign: "center" }}>
        <div style={{ marginBottom: "18px", display: "flex", justifyContent: "center" }}>
          <Logo size={22} />
        </div>
        {dateLabel && (
          <div style={{ fontSize: "0.8125rem", color: TEXT_MUTED, marginBottom: "16px", fontWeight: "600" }}>
            {dateLabel}
          </div>
        )}

        {loading ? (
          <p style={{ color: TEXT_MUTED, fontSize: "0.8125rem", padding: "30px" }}>loading...</p>
        ) : !stats || stats.totalGames === 0 ? (
          <p style={{ color: TEXT_MUTED, fontSize: "0.8125rem", padding: "30px" }}>{emptyLabel}</p>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "20px" }}>
              <OutcomeDonut cc={stats.cc} dd={stats.dd} cd={stats.cd} total={stats.totalGames} size={170} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
              {outcomes.map((o) => (
                <div key={o.label} style={{ textAlign: "center", padding: "10px", borderRadius: "8px", background: "#fff" }}>
                  <div style={{ display: "flex", justifyContent: "center", gap: "3px", marginBottom: "6px" }}>
                    <MoveIcon move={o.a} size={14} />
                    <MoveIcon move={o.b} size={14} flip={o.b === "defect"} />
                  </div>
                  <div style={{ fontSize: "1.0625rem", fontWeight: "700" }}>{o.count}</div>
                  <div style={{ fontSize: "0.625rem", color: TEXT_MUTED }}>{o.label}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Question/answer breakdown — its own card, only when that day
          actually had a question (every game that day shares the same one). */}
      {breakdown && (
        <div style={{ background: GRADIENT_BG, borderRadius: "16px", padding: "28px 20px", textAlign: "center", marginTop: "16px" }}>
          <div style={{ marginBottom: "14px", display: "flex", justifyContent: "center" }}>
            <Logo size={20} />
          </div>
          <div style={{ fontSize: "0.75rem", color: TEXT_MUTED, marginBottom: "4px" }}>today's question</div>
          <div style={{ fontSize: "0.9375rem", fontWeight: "700", marginBottom: "22px" }}>{breakdown.questionText}</div>

          <div style={{ display: "flex", justifyContent: "center", gap: "32px", marginBottom: "8px" }}>
            <AgreementBar label="answers matched" rate={agreedRate} count={breakdown.agreedTotal} color={TRUST_COLOR} />
            <AgreementBar label="answers differed" rate={disagreedRate} count={breakdown.disagreedTotal} color={MIXED_COLOR} />
          </div>
          <div style={{ fontSize: "0.6875rem", color: TEXT_MUTED, marginBottom: "22px" }}>% of those games where both players trusted</div>

          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: "0.75rem", fontWeight: "600", color: TEXT_MUTED, marginBottom: "10px" }}>every answer-pair permutation</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {breakdown.pairs.map((p) => (
                <div key={p.key} style={{ border: `1px solid ${BORDER}`, borderRadius: "10px", background: "#fff", padding: "14px", display: "flex", alignItems: "center", gap: "16px" }}>
                  {p.agree ? (
                    <OutcomeDonut cc={p.cc} dd={p.dd} cd={p.cd} total={p.total} size={92} centerLabel="games" />
                  ) : (
                    <Outcome2x2Matrix choices={p.choices} matrix={p.matrix} total={p.total} />
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "0.8125rem", fontWeight: "700", marginBottom: "6px" }}>
                      {p.agree ? `both said "${p.choices[0]}"` : `"${p.choices[0]}" vs "${p.choices[1]}"`}
                    </div>
                    {p.total === 0 ? (
                      <div style={{ fontSize: "0.75rem", color: TEXT_MUTED }}>no games yet</div>
                    ) : p.agree ? (
                      <div style={{ display: "flex", gap: "14px" }}>
                        <MiniStat icon={["cooperate", "cooperate"]} count={p.cc} color={TRUST_COLOR} />
                        <MiniStat icon={["cooperate", "defect"]} count={p.cd} color={MIXED_COLOR} />
                        <MiniStat icon={["defect", "defect"]} count={p.dd} color={BETRAY_COLOR} />
                      </div>
                    ) : (
                      <div style={{ fontSize: "0.6875rem", color: TEXT_MUTED }}>
                        rows: "{p.choices[0]}" picker's move · columns: "{p.choices[1]}" picker's move
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DailyOutcomesCard;
